import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import CustomTable from "../../Components/CustomTable";
import CustomModal from "../../Components/CustomModal";
import CustomPagination from "../../Components/CustomPagination";
import CustomButton from "../../Components/CustomButton";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useErrorBoundary } from "../../Hooks/useErrorBoundary";
import "./style.css";

export const FAQManagement = () => {
  const { handleError } = useErrorBoundary();
  const [faqs, setFaqs] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [statusUpdating, setStatusUpdating] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState({});

  const toastId = useRef(null);

  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    status: 1,
  });

  const token = localStorage.getItem("login") || "";
  const apiUrl = process.env.REACT_APP_BASE_URL;

  /* ===============================
     SAFE TOAST
  =============================== */

  const showToast = (type, message) => {

    if (toastId.current) toast.dismiss(toastId.current);

    toastId.current = toast[type](message, {
      autoClose: 2500,
    });

  };

  /* ===============================
     SAFE FETCH JSON
  =============================== */

  const safeParse = async (res) => {

    const contentType = res.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
      return await res.json();
    }

    const text = await res.text();
    throw new Error("Server error. Please try again later.");

  };

  /* ===============================
     FETCH FAQS
  =============================== */

  const fetchFaqs = async (page = 1) => {

    document.querySelector(".loaderBox")?.classList.remove("d-none");

    try {

      const res = await fetch(`${apiUrl}/admin/faqs?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await safeParse(res);

      if (data?.status) {
        setFaqs(data.data.data || []);
        setPagination(data.data);
        setCurrentPage(data.data.current_page || page);
      }

    } catch (error) {

      console.error(error);
      handleError(error);

    } finally {

      document.querySelector(".loaderBox")?.classList.add("d-none");

    }
  };

  useEffect(() => {
    fetchFaqs(currentPage);
  }, []);

  /* ===============================
     VALIDATION
  =============================== */

  const schema = Yup.object().shape({

    question: Yup.string()
      .min(5, "Question must be at least 5 characters")
      .required("Question is required"),

    answer: Yup.string()
      .min(10, "Answer must be at least 10 characters")
      .required("Answer is required"),

  });

  const validateForm = async (data) => {

    try {

      await schema.validate(data, { abortEarly: false });

      setErrors({});
      return true;

    } catch (err) {

      const validationErrors = {};

      if (err.inner) {
        err.inner.forEach((e) => {
          validationErrors[e.path] = e.message;
        });
      }

      setErrors(validationErrors);
      return false;

    }
  };

  /* ===============================
     ADD / UPDATE FAQ
  =============================== */

  const handleSubmit = async () => {

    if (submitting) return;

    const isValid = await validateForm(formData);

    if (!isValid) {
      showToast("error", "Please fix validation errors");
      return;
    }

    const loader = document.querySelector(".loaderBox");

    try {

      setSubmitting(true);

      loader?.classList.remove("d-none");

      const url = editingId
        ? `${apiUrl}/admin/faq-update/${editingId}`
        : `${apiUrl}/admin/faq`;

      const payload = {
        question: formData.question,
        answer: formData.answer,
        status: formData.status,
      };

      const res = await fetch(url, {

        method: "POST",

        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },

        body: JSON.stringify(payload),

      });

      const data = await safeParse(res);

      if (!res.ok) throw new Error(data?.message || "Something went wrong");

      showToast("success", data.message || "Success");

      setShowModal(false);

      setFormData({
        question: "",
        answer: "",
        status: 1,
      });

      setEditingId(null);

      await fetchFaqs(currentPage);

    } catch (err) {

      console.error(err);
      showToast("error", err.message);

    } finally {

      loader?.classList.add("d-none");
      setSubmitting(false);

    }

  };

  /* ===============================
     DELETE FAQ
  =============================== */

  const confirmDelete = async () => {

    const loader = document.querySelector(".loaderBox");

    try {

      loader?.classList.remove("d-none");

      const res = await fetch(`${apiUrl}/admin/faq/${editingId}`, {

        method: "DELETE",

        headers: {
          Authorization: `Bearer ${token}`,
        },

      });

      if (!res.ok) throw new Error();

      showToast("success", "FAQ Deleted Successfully");

      setShowDeleteModal(false);

      fetchFaqs(currentPage);

    } catch {

      showToast("error", "Delete failed");

    } finally {

      loader?.classList.add("d-none");

    }
  };

  /* ===============================
     STATUS TOGGLE
  =============================== */

  const toggleStatusInline = async (faq) => {

    if (statusUpdating === faq.id) return;

    document.querySelector(".loaderBox")?.classList.remove("d-none");

    try {

      setStatusUpdating(faq.id);

      const updatedStatus = faq.status === 1 ? 0 : 1;

      const res = await fetch(`${apiUrl}/admin/faq-update/${faq.id}`, {

        method: "POST",

        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },

        body: JSON.stringify({
          question: faq.question,
          answer: faq.answer,
          status: updatedStatus,
        }),

      });

      if (!res.ok) throw new Error();

      showToast("success", "Status Updated");

      fetchFaqs(currentPage);

    } catch {

      showToast("error", "Status update failed");

    } finally {

      setStatusUpdating(null);

      document.querySelector(".loaderBox")?.classList.add("d-none");

    }
  };

  /* ===============================
     RENDER
  =============================== */

  return (

    <DashboardLayout>

      <div className="dashCard faqPage">

        <div className="faqHeader">

          <h2 className="mainTitle">FAQ Management</h2>

          <CustomButton
            text="Add FAQ"
            variant="primaryButton"
            onClick={() => {

              setEditingId(null);

              setFormData({
                question: "",
                answer: "",
                status: 1,
              });

              setErrors({});
              setShowModal(true);

            }}
          />

        </div>

        <CustomTable
          headers={[
            { key: "id", title: "S.No" },
            { key: "question", title: "Question" },
            { key: "answer", title: "Answer" },
            { key: "status", title: "Status" },
            { key: "action", title: "Action" },
          ]}
        >

          <tbody>

            {faqs?.map((item, index) => (

              <tr key={item.id}>

                <td>{index + 1}</td>

                <td>{item?.question}</td>

                <td className="faqAnswer faqColumn">{item?.answer}</td>

                <td>

                  <label className="switch">

                    <input
                      type="checkbox"
                      checked={item.status === 1}
                      disabled={statusUpdating === item.id}
                      onChange={() => toggleStatusInline(item)}
                    />

                    <span className="slider"></span>

                  </label>

                </td>

                <td>

                  <div className="actionWrapper">

                    <button
                      className="actionBtn"
                      onClick={() => {

                        setEditingId(item.id);

                        setFormData({
                          question: item.question,
                          answer: item.answer,
                          status: item.status,
                        });

                        setShowModal(true);

                      }}
                    >
                      Edit
                    </button>

                    <button
                      className="actionBtn"
                      onClick={() => {

                        setEditingId(item.id);
                        setShowDeleteModal(true);

                      }}
                    >
                      Delete
                    </button>

                  </div>

                </td>

              </tr>

            ))}

          </tbody>

        </CustomTable>

        {pagination?.last_page > 1 && (
          <CustomPagination
            itemsPerPage={pagination.per_page}
            totalItems={pagination.total}
            currentPage={currentPage}
            onPageChange={(page) => fetchFaqs(page)}
          />
        )}

        <CustomModal
          show={showModal}
          close={() => setShowModal(false)}
          heading={editingId ? "Update FAQ" : "Add FAQ"}
        >

          <div className="modalForm animateModal">

            <input
              type="text"
              value={formData.question}
              placeholder="Enter question..."
              className="themeInput"
              onChange={(e) =>
                setFormData({ ...formData, question: e.target.value })
              }
            />

            {errors.question && (
              <p className="fieldError">{errors.question}</p>
            )}

            <textarea
              value={formData.answer}
              placeholder="Enter answer..."
              className="themeTextarea"
              onChange={(e) =>
                setFormData({ ...formData, answer: e.target.value })
              }
            />

            {errors.answer && (
              <p className="fieldError">{errors.answer}</p>
            )}

            <div className="toggleRow">

              <span>Status</span>

              <label className="switch">

                <input
                  type="checkbox"
                  checked={formData.status === 1}
                  onChange={() =>
                    setFormData({
                      ...formData,
                      status: formData.status === 1 ? 0 : 1,
                    })
                  }
                />

                <span className="slider"></span>

              </label>

            </div>

            <button
              type="button"
              className="submitBtn"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Submit"}
            </button>

          </div>

        </CustomModal>

        <CustomModal
          show={showDeleteModal}
          close={() => setShowDeleteModal(false)}
          action={confirmDelete}
          heading="Are you sure you want to delete this FAQ?"
        />

      </div>

    </DashboardLayout>

  );
};